import { useState } from "react";
import { PACKAGE_NAME, type PowerState } from "@comfy/shared";
import { useOnlineStatus } from "./useOnlineStatus.js";
import { useAuth, type UseAuthResult } from "./auth/useAuth.js";
import { usePower } from "./power/usePower.js";
import { PowerPanel } from "./power/PowerPanel.js";
import { useSubmit } from "./submit/useSubmit.js";
import { SubmitForm } from "./submit/SubmitForm.js";
import { WECHAT_COVER_DIMS } from "./submit/submitView.js";
import { useJobPolling } from "./job/useJobPolling.js";
import { JobViewer } from "./job/JobViewer.js";
import { useRecent } from "./recent/useRecent.js";
import { RecentList } from "./recent/RecentList.js";

// 应用外壳（App Shell）：竖屏单列触控布局（Req 9.1, 9.3）。
// 认证（9.2）与电源面板（9.3）已接入；其余 section（提交表单、任务轮询、看图、
// 最近列表、离线草稿）由后续任务填充。

const POWER_LABELS: Record<PowerState, string> = {
  running: "运行中",
  stopped: "已停机",
  starting: "启动中",
  stopping: "停止中",
  unavailable: "状态未知",
};

function OfflineBanner(): JSX.Element {
  // 离线提示骨架（由 Service Worker + 在线状态共同驱动）。
  return (
    <div className="offline-banner" role="status" aria-live="polite">
      <span className="offline-banner__dot" aria-hidden="true" />
      当前离线 · 已缓存草稿将在恢复后可继续提交
    </div>
  );
}

// 认证面板（任务 9.2）：登录 / 登出与会话过期提示。
// 认证状态由上层 App 通过 useAuth 提供并下传，保证与电源面板共享同一 authedFetch。
function AuthPanel({ auth }: { auth: UseAuthResult }): JSX.Element {
  const {
    configured,
    authenticated,
    loading,
    error,
    pendingCount,
    login,
    logout,
    reauthenticate,
  } = auth;

  if (!configured) {
    return (
      <section className="card" aria-label="登录">
        <h2 className="card__title">登录</h2>
        <p className="app__hint">
          未配置 Cognito（缺少 VITE_COGNITO_DOMAIN / VITE_USER_POOL_CLIENT_ID）。
          请参考 .env.example 配置后重试。
        </p>
      </section>
    );
  }

  if (loading) {
    return (
      <section className="card" aria-label="登录">
        <h2 className="card__title">登录</h2>
        <p className="app__hint">正在完成登录…</p>
      </section>
    );
  }

  if (authenticated) {
    return (
      <section className="card" aria-label="账户">
        <h2 className="card__title">账户</h2>
        <p className="app__hint">已登录 Owner 身份</p>
        <button type="button" className="btn" onClick={logout}>
          退出登录
        </button>
      </section>
    );
  }

  return (
    <section className="card" aria-label="登录">
      <h2 className="card__title">登录</h2>
      {error && (
        <p className="app__hint" role="alert">
          {error}
        </p>
      )}
      {pendingCount > 0 && (
        <p className="app__hint" role="status" aria-live="polite">
          有 {pendingCount} 个待提交请求将在重新登录后自动重放
        </p>
      )}
      <button
        type="button"
        className="btn btn--primary"
        onClick={pendingCount > 0 ? reauthenticate : login}
      >
        {pendingCount > 0 ? "重新登录" : "使用 Cognito 登录"}
      </button>
    </section>
  );
}

export function App(): JSX.Element {
  const online = useOnlineStatus();
  // 单一认证源：下传给认证面板与电源面板，共享 authedFetch（自动带 JWT）。
  const auth = useAuth();
  // 电源状态源（任务 9.3）：页头 pill 与电源面板共享，避免重复轮询。
  const power = usePower({
    authedFetch: auth.authedFetch,
    enabled: auth.authenticated,
  });
  // 当前正在查看 / 轮询的任务（提交成功后由 onSubmitted 设置，任务 9.5）。
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  // 最近任务列表（任务 9.7）：IndexedDB 持久化，提交成功即写入一条。
  const recent = useRecent();
  // 提交表单状态源（任务 9.4）：复用同一 authedFetch 与电源状态做提交门控。
  // onSubmitted 捕获新 jobId → 触发任务轮询（9.5）并写入最近列表（9.7）。
  const submit = useSubmit({
    authedFetch: auth.authedFetch,
    powerState: power.state,
    authenticated: auth.authenticated,
    onSubmitted: (jobId, request) => {
      setActiveJobId(jobId);
      void recent.add({
        jobId,
        prompt: request.positivePrompt,
        params: {
          width: request.width,
          height: request.height,
          steps: request.steps,
          guidance: request.guidance,
          seed: request.seed,
        },
        createdAt: Date.now(),
      });
    },
  });
  // 任务轮询状态源（任务 9.5）：按 activeJobId 轮询 GET /jobs/{id} 至 success/failed。
  const job = useJobPolling({
    authedFetch: auth.authedFetch,
    jobId: auth.authenticated ? activeJobId : null,
  });

  // 页头 pill：优先反映启动流程 / 冷加载态，否则展示服务端归一后的状态。
  const headerState: PowerState = power.state;

  return (
    <div className="app" data-package={PACKAGE_NAME}>
      <header className="app__header">
        <h1 className="app__title">HUANG · AI Art Studio</h1>
        <span
          className={`power-pill power-pill--${headerState}`}
          aria-label={`电源状态：${POWER_LABELS[headerState]}`}
        >
          {power.startingFlow ? "启动中" : POWER_LABELS[headerState]}
        </span>
      </header>

      {!online && <OfflineBanner />}

      <main className="app__main">
        <AuthPanel auth={auth} />
        <PowerPanel power={power} authenticated={auth.authenticated} />
        <SubmitForm submit={submit} authenticated={auth.authenticated} />
        <JobViewer job={job} authenticated={auth.authenticated} />
        <RecentList
          recent={recent}
          authenticated={auth.authenticated}
          onSelect={(jobId) => setActiveJobId(jobId)}
        />
        <p className="app__hint">
          默认尺寸 {WECHAT_COVER_DIMS.width}×{WECHAT_COVER_DIMS.height}（公众号封面）
        </p>
      </main>

      <footer className="app__footer">
        <small>单列竖屏触控外壳 · 功能将由后续任务接入</small>
      </footer>
    </div>
  );
}
